import merge from "lodash.merge";
import urlJoin from "url-join";
import stateService, { isStateKey, type State, setStateKey } from "../state";
import { isPopover, type Options } from "../types/options";
import { PrismaticMessageEvent } from "../types/postMessage";
import type { ScreenConfiguration } from "../types/screenConfiguration";
import { closePopover, openPopover } from "./popover";
import { postMessage } from "./postMessage";

export const EMBEDDED_ID = "pio__embedded";
export const EMBEDDED_IFRAME_ID = "pio__iframe";
export const EMBEDDED_IFRAME_PRELOAD_ID = "pio__iframe-preload";
export const EMBEDDED_STYLE_ID = "pio__styles";
export const EMBEDDED_IFRAME_CONTAINER_CLASS = "pio__iframeContainer";
export const EMBEDDED_IFRAME_CONTAINER_SELECTOR = `#${EMBEDDED_ID} .${EMBEDDED_IFRAME_CONTAINER_CLASS}`;
export const EMBEDDED_OVERLAY_CLASS = "pio__overlay";
export const EMBEDDED_OVERLAY_SELECTOR = `#${EMBEDDED_ID} > .${EMBEDDED_OVERLAY_CLASS}`;
export const EMBEDDED_OVERLAY_VISIBLE_CLASS = `${EMBEDDED_OVERLAY_CLASS}--is_visible`;
export const EMBEDDED_POPOVER_CLASS = "pio__popover";
export const EMBEDDED_POPOVER_CLOSE_CLASS = "pio__closeBtn";

export const getIframeContainerElement = (selector: string) =>
  document.querySelector(selector);

export const getIframeElement = (selector: string | undefined) =>
  document.querySelector(
    `${selector || EMBEDDED_IFRAME_CONTAINER_SELECTOR} > iframe`,
  );

export const isIframe = (
  element?: Element | null,
): element is HTMLIFrameElement =>
  Boolean(element && element.tagName === "IFRAME");

export const setIframe = (
  route: string,
  options: Options,
  params?: Record<string, string>,
) => {
  if (!isPopover(options) && !options.selector) {
    console.error("Could not find display selector.");
  }

  const iframeContainerElement = getIframeContainerElement(
    isPopover(options) ? EMBEDDED_IFRAME_CONTAINER_SELECTOR : options.selector,
  );

  if (!iframeContainerElement) {
    return;
  }

  // we use a copy of state so that changes to this only impact this iframe and do not update the shared state
  const state = stateService.getStateCopy();

  if (options) {
    Object.entries(options).forEach(([key, value]) => {
      if (isStateKey(key)) {
        const existing = state[key];
        const next =
          existing instanceof Object ? merge(existing, value) : value;
        setStateKey(state, key, next as State[typeof key]);
      }
    });
  }

  const queryParams = new URLSearchParams({
    ...params,
    embed: "true",
  });

  if (state.theme) {
    queryParams.set("theme", state.theme);
  }

  if (state.screenConfiguration?.initializing) {
    queryParams.set(
      "initializing",
      JSON.stringify(state.screenConfiguration.initializing),
    );
  }

  if (state.fontConfiguration) {
    queryParams.set(
      "fontConfiguration",
      JSON.stringify(state.fontConfiguration),
    );
  }

  const iframeSrc = `${urlJoin(state.prismaticUrl, route)}?${queryParams}`;

  iframeContainerElement.innerHTML = /* html */ `
    <iframe
      id="${EMBEDDED_IFRAME_ID}"
      src="${iframeSrc}"
      height="100%"
      width="100%"
      frameBorder="0"
      allow="clipboard-read; clipboard-write"
    ></iframe>
  `;

  const iframeElement = iframeContainerElement.querySelector("iframe");

  if (iframeElement) {
    const abortController = new AbortController();
    observeForIframeRemoval(abortController);
    window.addEventListener(
      "message",
      (event: MessageEvent<{ event: string }>) => {
        if (event.data?.event === "PRISMATIC_INITIALIZED") {
          postMessage({
            iframe: iframeElement,
            event: {
              event: PrismaticMessageEvent.SET_TOKEN,
              data: state.jwt,
            },
          });

          postMessage({
            iframe: iframeElement,
            event: {
              event: PrismaticMessageEvent.SET_VERSION,
              data: EMBEDDED_VERSION,
            },
          });

          postMessage({
            iframe: iframeElement,
            event: {
              event: PrismaticMessageEvent.SET_TRANSLATION,
              data: state.translation,
            },
          });

          const screenConfigurationToPost: ScreenConfiguration | undefined =
            isPopover(options)
              ? {
                  ...(state.screenConfiguration ?? {}),
                  isInPopover: true,
                }
              : state.screenConfiguration;
          postMessage({
            iframe: iframeElement,
            event: {
              event: PrismaticMessageEvent.SET_SCREEN_CONFIGURATION,
              data: screenConfigurationToPost,
            },
          });

          postMessage({
            iframe: iframeElement,
            event: {
              event: PrismaticMessageEvent.SET_FILTERS,
              data: state.filters,
            },
          });

          postMessage({
            iframe: iframeElement,
            event: {
              event: PrismaticMessageEvent.SET_AGENT_CONTEXT,
              data: state.agent?.context,
            },
          });
        } else if (
          event.data?.event === PrismaticMessageEvent.POPOVER_CLOSE_REQUESTED
        ) {
          closePopover();
        }
      },
      {
        signal: abortController.signal,
      },
    );

    if (options?.autoFocusIframe !== false) {
      iframeElement.addEventListener(
        "mouseenter",
        () => {
          iframeElement.focus();
        },
        {
          signal: abortController.signal,
        },
      );
    }
  }

  if (isPopover(options)) {
    openPopover();
  }
};

const observeForIframeRemoval = (signal: AbortController) => {
  const config = { attributes: false, childList: true, subtree: true };

  // Create an observer instance that will self-terminate once the iframe that we care about has been removed
  const observer = new MutationObserver((mutationList, observer) => {
    for (const mutation of mutationList) {
      if (mutation.type === "childList") {
        const removedNodes = Array.from(mutation.removedNodes);
        for (const removedNode of removedNodes) {
          if (removedNode.nodeType === Node.ELEMENT_NODE) {
            if (
              (removedNode as HTMLElement).tagName === "IFRAME" &&
              (removedNode as HTMLIFrameElement).id === EMBEDDED_IFRAME_ID
            ) {
              signal.abort();
              observer.disconnect();
            } else {
              const iframe = (removedNode as HTMLElement).querySelector(
                `iframe#${EMBEDDED_IFRAME_ID}`,
              );
              if (iframe !== null) {
                signal.abort();
                observer.disconnect();
              }
            }
          }
        }
      }
    }
  });

  // Start observing the target node for configured mutations
  observer.observe(document.body, config);
};
